import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Plus,
  Upload,
  Image as ImageIcon,
  Info,
  MessageSquare,
  Calendar,
  Loader2,
  ChevronDown,
  Camera,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  VehicleTestImages,
  VehicleTestImageFiles,
  ExteriorImage,
  DamageImage,
  ServiceDetail,
  UpdateVehicleTestImagesRequest,
  is360ExteriorFrame,
} from "@/types/vehicleTest";
import { ModalInfoVehicleImages } from "./components/ModalInfoVehicleImages";
import { isVideoMedia } from "@/lib/isVideoMedia";
import {
  ServiceDetailsDialog,
  ServiceDetailsViewDialog,
} from "./components/ServiceDetailsDialog";
import { format } from "date-fns";
import { FileObject } from "@/types/shared/file";
import {
  buildVehicleTestImageFiles,
  AddedImage,
} from "@/selectors/vehicleTestSelectors";
import { CarTestService } from "@/services/carTest/carTestService";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { isIOS, isAndroid } from "@/utils/platformUtils";
import { ImagePlus } from "lucide-react";
import { ImageUploadButton } from "./components/ImageUploadButton";
import { ImageSectionTips } from "./components/ImageSectionTips";
import { SimpleImageSection } from "./components/sections/SimpleImageSection";
import { ExteriorSection } from "./components/sections/ExteriorSection";
import { applyExteriorThumbnailFlags } from "./components/sections/exteriorImageHelpers";
import { DamageSection } from "./components/sections/DamageSection";
import { ServiceSection } from "./components/sections/ServiceSection";
import { TireSection } from "./components/sections/TireSection";
import {
  isSectionValid,
  getValidationErrors,
} from "./components/imageValidation";
import { useImageUpload } from "./hooks/useImageUpload";
import { useListingPhotoUploadQueue, type ListingPhotoQueueItem } from "@/hooks/useListingPhotoUploadQueue";
import { PhotoSessionModal } from "./photoSession/PhotoSessionModal";
import {
  getSectionImageCount,
  getSectionMax,
  getSectionTitleKey,
  type PhotoSessionSection,
} from "./photoSession/photoSessionUtils";
import { psLog, psWarn } from "./photoSession/photoSessionDebug";
import { useMedia } from "@/hooks/useMedia";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { usePhotoGuide } from "@/hooks/useSupportGuides";
import {
  normalizeSupportGuideVideoUrl,
  EXTERIOR_MAX_REGULAR,
} from "@/config/constants";
import VideoPlayer from "@/components/support/VideoPlayer";
import FullscreenGallery from "@/components/car-listing/tire-info/FullscreenGallery";
import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";


export type VehicleImageUploadSectionHandle = {
  savePendingImages: () => Promise<void>;
  /** Clear pending service details after they were persisted via createTest. */
  clearPendingServiceDetails: () => void;
};

interface VehicleImageUploadSectionProps {
  onNext: () => void | Promise<void>;
  onChange: (data: VehicleTestImages) => void;
  initialData?: VehicleTestImages;
  isEditMode?: boolean;
  onImageFilesChange?: (files: VehicleTestImageFiles) => void;
  onSaveImageChanges?: (data: UpdateVehicleTestImagesRequest) => void;
  /** Called after full save (add/remove + reorder + rotate) so parent can invalidate React Query cache */
  onAfterImageSaveComplete?: (testId: string) => void;
  testId?: string;
  hideIntegratedImageSaveButton?: boolean;
  onImageDirtyChange?: (dirty: boolean) => void;
}

/** Keep first occurrence per id/imageId so API never shows duplicate slots after refresh */
function dedupeImagesById<T extends { id?: string; imageId?: string }>(
  arr: T[] | undefined,
): T[] {
  if (!arr?.length) return arr ?? [];
  const seen = new Set<string>();
  return arr.filter((item) => {
    const id = item.id ?? item.imageId ?? "";
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

const VehicleImageUploadSection = forwardRef<
  VehicleImageUploadSectionHandle,
  VehicleImageUploadSectionProps
>(function VehicleImageUploadSection(
  {
    onNext,
    onChange,
    initialData = {} as VehicleTestImages,
    isEditMode = false,
    onImageFilesChange,
    onSaveImageChanges,
    onAfterImageSaveComplete,
    testId,
    hideIntegratedImageSaveButton = false,
    onImageDirtyChange,
  },
  ref,
) {
  const { language } = useLanguage();
  const lp = listingPostLabels(language);
  const isMobile = useIsMobile();
  const { getDownloadUrlAsync } = useMedia();
  const { data: photoGuide } = usePhotoGuide();
  /** Only show "Bild guide" video when backend has a guide with showOnImageUploadGuide=true and isPublished=true */
  const hasPhotoGuide = !!(photoGuide?.videoUrl && photoGuide?.isPublished);
  const [currentImages, setCurrentImages] =
    useState<VehicleTestImages>(initialData);
  const currentImagesForRemoveRef = useRef(currentImages);
  currentImagesForRemoveRef.current = currentImages;

  const [hasChanges, setHasChanges] = useState(false);
  const [addedImages, setAddedImages] = useState<AddedImage[]>([]);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [addedServiceDetails, setAddedServiceDetails] = useState<
    ServiceDetail[]
  >([]);
  const [removedServiceDetails, setRemovedServiceDetails] = useState<string[]>(
    [],
  );
  const [updatedDamageComments, setUpdatedDamageComments] = useState<
    { imageId: string; comment: string }[]
  >([]);
  /** I Redigera test: sparad ordning per sektion, skickas vid "Spara bildändringar" */
  const [pendingReorders, setPendingReorders] = useState<
    Partial<
      Record<
        | "exterior"
        | "interior"
        | "damage"
        | "serviceImages"
        | "tiresPrimary"
        | "tiresSecondary",
        string[]
      >
    >
  >({});
  /** Ref så att knappen alltid känner av reorder även om effect körs i fel ordning */
  const hasPendingReordersRef = useRef(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    alt: string;
    index: number;
  } | null>(null);
  const [allImages, setAllImages] = useState<
    Array<{ url: string; alt: string; imageId?: string; type?: string }>
  >([]);
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(
    new Map(),
  );
  const [showServiceDetailDialog, setShowServiceDetailDialog] = useState(false);
  const [editingServiceDetail, setEditingServiceDetail] =
    useState<ServiceDetail | null>(null);
  const [expandedComments, setExpandedComments] = useState<{
    [key: number]: boolean;
  }>({});
  const [commentTexts, setCommentTexts] = useState<{
    [key: number]: string;
  }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingBeforeNext, setIsSavingBeforeNext] = useState(false);
  const commentTextsRef = useRef<{ [key: number]: string }>({});
  const [uploadingImages, setUploadingImages] = useState<Set<string>>(
    new Set(),
  );
  // Separate tracking for thumbnail vs regular images in exterior section
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingRegularExterior, setUploadingRegularExterior] =
    useState(false);
  const [uploadingEngineVideo, setUploadingEngineVideo] = useState(false);
  const [showPhotoGuideVideo, setShowPhotoGuideVideo] = useState(false);
  const [photoGuideOpen, setPhotoGuideOpen] = useState(false);
  const photoGuideSheetProps = nativeNestedSheetDialogProps(photoGuideOpen);
  const [rotatingImageId, setRotatingImageId] = useState<string | null>(null);
  /** Session-only rotation in degrees (90, 180, 270). Saved to backend only on "Spara bildändringar". */
  const [rotationByImageId, setRotationByImageId] = useState<
    Record<string, number>
  >({});

  // Use ref to store the latest onChange function to avoid re-render loops
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const previousImagesRef = useRef<VehicleTestImages>({} as VehicleTestImages);
  const previousTestIdRef = useRef<string | undefined>(testId);
  const isFirstMountRef = useRef(true);

  const getStableImageId = useCallback((img: unknown): string | undefined => {
    if (img == null || typeof img !== "object") return undefined;
    const rec = img as Record<string, unknown>;
    const id = rec["id"];
    const imageId = rec["imageId"];
    const raw =
      id !== undefined && id !== null && id !== ""
        ? id
        : imageId !== undefined && imageId !== null && imageId !== ""
          ? imageId
          : undefined;
    if (raw === undefined) return undefined;
    return String(raw);
  }, []);
  // Track successfully processed images for iOS/Android verification
  const processedImagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Only reset tracking arrays if testId changes (different test loaded)
    // Don't reset when initialData updates from onChange callback
    const currentTestId = testId;
    const previousTestId = previousTestIdRef.current;

    // Normalize initialData: map damageComment to comment, dedupe by id (avoid duplicate slots after refresh)
    const normalizedInitialData = { ...initialData };
    if (normalizedInitialData.damage) {
      normalizedInitialData.damage = normalizedInitialData.damage.map(
        (img: any) => ({
          ...img,
          comment: img.comment || img.damageComment || null, // Support both comment and damageComment
        }),
      );
    }
    normalizedInitialData.exterior = dedupeImagesById(
      normalizedInitialData.exterior,
    );
    normalizedInitialData.interior = dedupeImagesById(
      normalizedInitialData.interior,
    );
    normalizedInitialData.damage = dedupeImagesById(
      normalizedInitialData.damage,
    );
    normalizedInitialData.serviceImages = dedupeImagesById(
      normalizedInitialData.serviceImages,
    );
    if (normalizedInitialData.tires) {
      normalizedInitialData.tires = {
        primary: dedupeImagesById(normalizedInitialData.tires.primary),
        secondary: dedupeImagesById(normalizedInitialData.tires.secondary),
      };
    }

    if (isFirstMountRef.current) {
      // First mount - set currentImages and initialize refs
      // Sort exterior images to ensure thumbnail comes first
      if (
        normalizedInitialData.exterior &&
        Array.isArray(normalizedInitialData.exterior)
      ) {
        normalizedInitialData.exterior = [
          ...normalizedInitialData.exterior,
        ].sort((a, b) => {
          // Thumbnail (isThumbnail=true) comes first
          if (a.isThumbnail && !b.isThumbnail) return -1;
          if (!a.isThumbnail && b.isThumbnail) return 1;
          return 0; // Keep original order for non-thumbnail images
        });
      }
      setCurrentImages(normalizedInitialData);
      previousTestIdRef.current = currentTestId;
      isFirstMountRef.current = false;
      return;
    }

    if (currentTestId !== previousTestId) {
      // Different test loaded - reset everything (dedupe already applied above)
      // Sort exterior images to ensure thumbnail comes first
      if (
        normalizedInitialData.exterior &&
        Array.isArray(normalizedInitialData.exterior)
      ) {
        normalizedInitialData.exterior = [
          ...normalizedInitialData.exterior,
        ].sort((a, b) => {
          // Thumbnail (isThumbnail=true) comes first
          if (a.isThumbnail && !b.isThumbnail) return -1;
          if (!a.isThumbnail && b.isThumbnail) return 1;
          return 0; // Keep original order for non-thumbnail images
        });
      }
      setCurrentImages(normalizedInitialData);
      setAddedImages([]);
      setRemovedImages([]);
      setUpdatedDamageComments([]);
      setAddedServiceDetails([]);
      setRemovedServiceDetails([]);
      setPendingReorders({});
      setRotationByImageId({});
      setHasChanges(false);
      hasPendingReordersRef.current = false;
      processedImagesRef.current.clear(); // Clear processed images tracking
      previousTestIdRef.current = currentTestId;
    }
    // If testId is the same, don't reset tracking arrays
    // This allows onChange to update initialData without resetting tracking
    // Note: We don't update currentImages here when testId is the same,
    // because currentImages is managed by addImageToState and other functions
  }, [initialData, testId]);

  // Fetch preview URLs for all images on mount (for edit mode - show preview thumbnails)
  useEffect(() => {
    if (!isEditMode) return;

    const fetchPreviewUrls = async () => {
      const allImages: any[] = [
        ...(currentImages?.exterior || []),
        ...(currentImages?.interior || []),
        ...(currentImages?.damage || []),
        ...(currentImages?.serviceImages || []),
        ...(currentImages?.tires?.primary || []),
        ...(currentImages?.tires?.secondary || []),
      ];

      const toFetch = allImages.filter(
        (img) => img.previewMediaId && img.previewMediaId !== img.mediaId,
      );

      const previewPromises = toFetch.map(async (img) => {
        try {
          const previewUrl = await getDownloadUrlAsync({
            mediaId: img.previewMediaId,
            expiresIn: 3600,
          });
          return { mediaId: img.previewMediaId, url: previewUrl };
        } catch (error) {
          return null;
        }
      });

      const results = await Promise.all(previewPromises);
      const urlMap = new Map<string, string>();
      results.forEach((result) => {
        if (result) {
          urlMap.set(result.mediaId, result.url);
        }
      });
      setPreviewUrls(urlMap);
    };

    fetchPreviewUrls();
  }, [currentImages, isEditMode, getDownloadUrlAsync]);

  // Update hasChanges when any tracking arrays change
  useEffect(() => {
    const pendingCount = Object.keys(pendingReorders).length;
    const hasRotationChanges = Object.values(rotationByImageId).some(
      (deg) => deg !== 0,
    );
    const hasAnyChanges =
      addedImages.length > 0 ||
      removedImages.length > 0 ||
      addedServiceDetails.length > 0 ||
      removedServiceDetails.length > 0 ||
      updatedDamageComments.length > 0 ||
      pendingCount > 0 ||
      hasRotationChanges;

    if (pendingCount > 0) hasPendingReordersRef.current = true;
    setHasChanges(hasAnyChanges);
  }, [
    addedImages,
    removedImages,
    addedServiceDetails,
    removedServiceDetails,
    updatedDamageComments,
    pendingReorders,
    rotationByImageId,
    isEditMode,
    testId,
  ]);

  // Thumbnail must always show PREVIEW (small). Only use image.url when there is no separate preview (legacy).
  const getThumbnailUrl = (image: any): string => {
    if (isVideoMedia(image?.type, image?.url)) {
      return image.url || "";
    }
    if (
      image.previewMediaId != null &&
      image.previewMediaId !== image.mediaId
    ) {
      return previewUrls.get(image.previewMediaId) || image.url || "";
    }
    return image.url || "";
  };

  // Handle image click - use preview for thumbnails, fullSize on click
  const handleImageClick = async (image: any, alt: string) => {
    let imageUrl = image.url || "";
    const clickedIsVideo = isVideoMedia(image?.type, image?.url);

    // PRIORITY 1: For images in create mode (not edit mode), ALWAYS try to use originalFile FIRST
    // This ensures we show the full-size version, not the compressed preview
    // This works for both temp-ID images and eager-uploaded images (with real IDs)
    // Skip for engine video — keep streaming URL (data: URLs are huge / flaky in lightbox).
    if (!clickedIsVideo && !isEditMode && addedImages.length > 0) {
      // Try to find the image in addedImages by multiple methods
      let addedImage = addedImages.find((img) => {
        // Match by image ID first (most reliable)
        if (img.imageId === image.id) {
          return true;
        }
        return false;
      });

      // If no match by ID, try matching by section and position/index
      if (!addedImage) {
        // Determine which section this image belongs to and its index
        let section: string | undefined;
        let isThumbnail: boolean | undefined;
        let imageIndex: number = -1;

        // Check exterior images
        if (currentImages.exterior) {
          const exteriorIndex = currentImages.exterior.findIndex(
            (img: any) => img.id === image.id,
          );
          if (exteriorIndex !== -1) {
            section = "exterior";
            isThumbnail = currentImages.exterior[exteriorIndex].isThumbnail;
            imageIndex = exteriorIndex;
          }
        }

        // Check interior images
        if (!section && currentImages.interior) {
          const interiorIndex = currentImages.interior.findIndex(
            (img: any) => img.id === image.id,
          );
          if (interiorIndex !== -1) {
            section = "interior";
            imageIndex = interiorIndex;
          }
        }

        // Check damage images
        if (!section && currentImages.damage) {
          const damageIndex = currentImages.damage.findIndex(
            (img: any) => img.id === image.id,
          );
          if (damageIndex !== -1) {
            section = "damage";
            imageIndex = damageIndex;
          }
        }

        // Check serviceImages
        if (!section && currentImages.serviceImages) {
          const serviceIndex = currentImages.serviceImages.findIndex(
            (img: any) => img.id === image.id,
          );
          if (serviceIndex !== -1) {
            section = "serviceImages";
            imageIndex = serviceIndex;
          }
        }

        // Check tires
        if (!section && currentImages.tires) {
          const allTires = [
            ...(currentImages.tires.primary || []),
            ...(currentImages.tires.secondary || []),
          ];
          const tireIndex = allTires.findIndex(
            (img: any) => img.id === image.id,
          );
          if (tireIndex !== -1) {
            section = "tires";
            const primaryCount = (currentImages.tires.primary || []).length;
            if (tireIndex < primaryCount) {
              imageIndex = tireIndex;
            } else {
              imageIndex = tireIndex - primaryCount;
            }
          }
        }

        if (section) {
          // Find matching image in addedImages by section and position
          const sectionImages = addedImages.filter(
            (img) => img.section === section,
          );

          if (section === "exterior" && isThumbnail !== undefined) {
            // For exterior, match by thumbnail status first
            const thumbnailMatch = sectionImages.find(
              (img) => img.isThumbnail === isThumbnail,
            );
            if (thumbnailMatch) {
              addedImage = thumbnailMatch;
            } else if (imageIndex >= 0) {
              // For exterior, count thumbnails separately
              const thumbnailsInAdded = sectionImages.filter(
                (img) => img.isThumbnail === true,
              );
              const regularInAdded = sectionImages.filter(
                (img) => img.isThumbnail === false,
              );

              if (isThumbnail && thumbnailsInAdded.length > 0) {
                addedImage = thumbnailsInAdded[0];
              } else if (
                !isThumbnail &&
                imageIndex >= 0 &&
                imageIndex < regularInAdded.length
              ) {
                addedImage = regularInAdded[imageIndex];
              }
            }
          } else if (imageIndex >= 0 && imageIndex < sectionImages.length) {
            // For other sections, match by index
            addedImage = sectionImages[imageIndex];
          }

          if (!addedImage && sectionImages.length > 0) {
            // Last resort: use first image in section
            addedImage = sectionImages[0];
          }
        }

        if (!addedImage) {
        }
      }

      if (addedImage) {
        // ALWAYS use originalFile if available (full-size), otherwise fall back to compressed file
        const fileToUse = addedImage.originalFile || addedImage.file;

        if (fileToUse) {
          const isOriginal = fileToUse === addedImage.originalFile;

          // Create a new data URL from the original file (not compressed)
          try {
            const reader = new FileReader();
            imageUrl = await new Promise<string>((resolve, reject) => {
              reader.onload = (e) => {
                const url = e.target?.result as string;
                if (url) {
                  resolve(url);
                } else {
                  reject(new Error("Failed to read file"));
                }
              };
              reader.onerror = (error) => {
                reject(new Error("Failed to read file"));
              };
              reader.readAsDataURL(fileToUse);
            });
          } catch (error) {
            // Fallback to existing URL
          }
        } else {
        }
      } else {
      }
    }
    // PRIORITY 2: If fullSizeMediaId exists (for existing uploaded images in edit mode), fetch fullSize URL
    else if (image.fullSizeMediaId && image.fullSizeMediaId !== image.mediaId) {
      try {
        imageUrl = await getDownloadUrlAsync({
          mediaId: image.fullSizeMediaId,
          expiresIn: 3600,
        });
      } catch (error) {
        // Continue with existing URL if fullSize fetch fails
      }
    }

    // Collect all images for gallery navigation (include type so video plays in lightbox)
    const images: Array<{
      url: string;
      alt: string;
      imageId?: string;
      type?: string;
    }> = [];

    // Exterior images
    currentImages.exterior?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.exterior} ${idx + 1}`,
          imageId: getStableImageId(img),
          type: img.type,
        });
      }
    });

    // Interior images
    currentImages.interior?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.interior} ${idx + 1}`,
          imageId: getStableImageId(img),
          type: img.type,
        });
      }
    });

    // Damage images
    currentImages.damage?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.damageImageAlt || lp.images.damage} ${idx + 1}`,
          imageId: getStableImageId(img),
          type: img.type,
        });
      }
    });

    // Service images
    currentImages.serviceImages?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.service} ${idx + 1}`,
          imageId: getStableImageId(img),
          type: img.type,
        });
      }
    });

    // Primary tires
    if (Array.isArray(currentImages.tires?.primary)) {
      currentImages.tires.primary.forEach((tire, idx) => {
        if (tire.url) {
          images.push({
            url: tire.url,
            alt: `${lp.images.tirePrimaryAlt || lp.images.primary} ${idx + 1}`,
            imageId: getStableImageId(tire),
            type: tire.type,
          });
        }
      });
    }

    // Secondary tires
    if (Array.isArray(currentImages.tires?.secondary)) {
      currentImages.tires.secondary.forEach((tire, idx) => {
        if (tire.url) {
          images.push({
            url: tire.url,
            alt: `${lp.images.tireSecondaryAlt || lp.images.secondary} ${idx + 1}`,
            imageId: getStableImageId(tire),
            type: tire.type,
          });
        }
      });
    }

    setAllImages(images);

    // Prefer matching by image id so data-URL / signed-URL swaps still find the video
    let index = -1;
    const clickedId = getStableImageId(image);
    if (clickedId) {
      index = images.findIndex((img) => img.imageId === clickedId);
    }
    if (index === -1) {
      index = images.findIndex((img) => img.url === imageUrl);
    }

    // If not found, try to find by comparing URLs more flexibly
    if (index === -1) {
      index = images.findIndex((img) => {
        return (
          img.url === imageUrl ||
          imageUrl.includes(img.url) ||
          img.url.includes(imageUrl)
        );
      });
    }

    // If still not found, use 0 as fallback
    if (index === -1) {
      index = 0;
    }

    setSelectedImage({
      url: imageUrl,
      alt,
      index,
    });
  };

  // Helper function to get full URL for an image (similar to handleImageClick but without changing allImages)
  const getFullImageUrl = async (image: any): Promise<string> => {
    let imageUrl = image.url || "";

    // For images in create mode, try to get originalFile from addedImages
    if (!isEditMode && addedImages.length > 0) {
      const addedImage = addedImages.find((img) => img.imageId === image.id);
      if (addedImage) {
        const fileToUse = addedImage.originalFile || addedImage.file;
        if (fileToUse) {
          try {
            const reader = new FileReader();
            imageUrl = await new Promise<string>((resolve, reject) => {
              reader.onload = (e) => {
                resolve(e.target?.result as string);
              };
              reader.onerror = reject;
              reader.readAsDataURL(fileToUse);
            });
          } catch (error) {}
        }
      }
    }
    // For edit mode, try to get fullSizeMediaId
    else if (image.fullSizeMediaId && image.fullSizeMediaId !== image.mediaId) {
      try {
        imageUrl = await getDownloadUrlAsync({
          mediaId: image.fullSizeMediaId,
          expiresIn: 3600,
        });
      } catch (error) {}
    }

    return imageUrl;
  };

  // Navigate to previous image
  const navigatePrev = async () => {
    if (!selectedImage || selectedImage.index <= 0) return;

    const prevIndex = selectedImage.index - 1;
    const prevImage = allImages[prevIndex];
    if (!prevImage) return;

    // Find the image in currentImages structure to get full URL
    const allCurrentImages = [
      ...(currentImages.exterior || []),
      ...(currentImages.interior || []),
      ...(currentImages.damage || []),
      ...(currentImages.serviceImages || []),
      ...(currentImages.tires?.primary || []),
      ...(currentImages.tires?.secondary || []),
    ];

    // Try to find by URL match
    const foundImage: any = allCurrentImages.find((img) => {
      if (!img.url) return false;
      return (
        img.url === prevImage.url ||
        prevImage.url.includes(img.url) ||
        img.url.includes(prevImage.url)
      );
    });

    // Get full URL for the image
    const fullUrl = foundImage
      ? await getFullImageUrl(foundImage)
      : prevImage.url;

    // Update selectedImage with correct index and full URL
    setSelectedImage({
      url: fullUrl,
      alt: prevImage.alt,
      index: prevIndex,
    });
  };

  // Navigate to next image
  const navigateNext = async () => {
    if (!selectedImage || selectedImage.index >= allImages.length - 1) return;

    const nextIndex = selectedImage.index + 1;
    const nextImage = allImages[nextIndex];
    if (!nextImage) return;

    // Find the image in currentImages structure to get full URL
    const allCurrentImages = [
      ...(currentImages.exterior || []),
      ...(currentImages.interior || []),
      ...(currentImages.damage || []),
      ...(currentImages.serviceImages || []),
      ...(currentImages.tires?.primary || []),
      ...(currentImages.tires?.secondary || []),
    ];

    // Try to find by URL match
    const foundImage: any = allCurrentImages.find((img) => {
      if (!img.url) return false;
      return (
        img.url === nextImage.url ||
        nextImage.url.includes(img.url) ||
        img.url.includes(nextImage.url)
      );
    });

    // Get full URL for the image
    const fullUrl = foundImage
      ? await getFullImageUrl(foundImage)
      : nextImage.url;

    // Update selectedImage with correct index and full URL
    setSelectedImage({
      url: fullUrl,
      alt: nextImage.alt,
      index: nextIndex,
    });
  };

  const closeImagePreview = useCallback(() => setSelectedImage(null), []);
  const lightboxUrls = useMemo(
    () => allImages.map((img) => img.url),
    [allImages],
  );
  const lightboxMediaTypes = useMemo(
    () => allImages.map((img) => img.type),
    [allImages],
  );
  const handleFullscreenSelect = useCallback(
    (index: number) => {
      const img = allImages[index];
      if (img) {
        setSelectedImage({ url: img.url, alt: img.alt, index });
      }
    },
    [allImages],
  );

  const reorderSection = useCallback(
    (
      section: keyof VehicleTestImages,
      newOrder: (ExteriorImage | DamageImage | FileObject)[],
      setter: (prev: VehicleTestImages) => VehicleTestImages,
      tireSubSection?: "primary" | "secondary",
    ) => {
      const toIds = (arr: unknown[]) =>
        arr
          .map(getStableImageId)
          .filter((id): id is string => !!id && !id.startsWith("temp-"));

      setCurrentImages(setter);
      if (!testId) return;

      // For tires, backend expects ALL tire picture IDs in display order (primary then secondary)
      // so that position is unique across WHEELS and CarDetails > Hjul loads in correct order.
      let ids: string[];
      if (section === "tires" && tireSubSection) {
        const primaryList =
          tireSubSection === "primary"
            ? newOrder
            : (currentImages.tires?.primary ?? []);
        const secondaryList =
          tireSubSection === "secondary"
            ? newOrder
            : (currentImages.tires?.secondary ?? []);
        ids = [...toIds(primaryList), ...toIds(secondaryList)];
      } else {
        ids = toIds(newOrder);
      }

      const reorderKey =
        section === "tires"
          ? tireSubSection === "primary"
            ? "tiresPrimary"
            : "tiresSecondary"
          : (section as keyof typeof pendingReorders);

      // I Redigera test ELLER skapa-läge med testId: spara lokalt, skicka vid "Spara bildändringar"
      if (isEditMode || testId) {
        setPendingReorders((prev) => ({ ...prev, [reorderKey]: ids }));
        hasPendingReordersRef.current = true;
        setHasChanges(true);
        return;
      }

      // Fallback: om inget testId (tidigt skapa-läge), ingen API-anrop
      if (ids.length === 0) return;
      CarTestService.reorderCarTestImages(testId!, ids)
        .then(() =>
          toast.success(lp.images?.imageOrderSaved ?? "Ordning sparad"),
        )
        .catch((err) => {
          toast.error(lp.images?.imageOrderError ?? "Kunde inte spara ordning");
        });
    },
    [testId, isEditMode, lp.images, currentImages.tires, getStableImageId],
  );

  // Update allImages when currentImages changes (for gallery navigation; include imageId for rotation in popup)
  useEffect(() => {
    const images: Array<{
      url: string;
      alt: string;
      imageId?: string;
      type?: string;
    }> = [];

    // Exterior images
    currentImages.exterior?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.exterior} ${idx + 1}`,
          imageId: getStableImageId(img),
          type: img.type,
        });
      }
    });

    // Interior images
    currentImages.interior?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.interior} ${idx + 1}`,
          imageId: getStableImageId(img),
        });
      }
    });

    // Damage images
    currentImages.damage?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.damageImageAlt || lp.images.damage} ${idx + 1}`,
          imageId: getStableImageId(img),
        });
      }
    });

    // Service images
    currentImages.serviceImages?.forEach((img, idx) => {
      if (img.url) {
        images.push({
          url: img.url,
          alt: `${lp.images.service} ${idx + 1}`,
          imageId: getStableImageId(img),
        });
      }
    });

    // Primary tires
    if (Array.isArray(currentImages.tires?.primary)) {
      currentImages.tires.primary.forEach((tire, idx) => {
        if (tire.url) {
          images.push({
            url: tire.url,
            alt: `${lp.images.tirePrimaryAlt || lp.images.primary} ${idx + 1}`,
            imageId: getStableImageId(tire),
          });
        }
      });
    }

    // Secondary tires
    if (Array.isArray(currentImages.tires?.secondary)) {
      currentImages.tires.secondary.forEach((tire, idx) => {
        if (tire.url) {
          images.push({
            url: tire.url,
            alt: `${lp.images.tireSecondaryAlt || lp.images.secondary} ${idx + 1}`,
            imageId: getStableImageId(tire),
          });
        }
      });
    }

    setAllImages(images);

    // Update selectedImage index if it's out of bounds
    if (
      selectedImage &&
      selectedImage.index >= images.length &&
      images.length > 0
    ) {
      setSelectedImage({ ...selectedImage, index: images.length - 1 });
    }
  }, [currentImages, lp.images]);

  // Build VehicleTestImageFiles structure using selector
  const buildImageFilesStructure = useCallback((): VehicleTestImageFiles => {
    return buildVehicleTestImageFiles(addedImages, addedServiceDetails);
  }, [addedImages, addedServiceDetails]);

  // Update parent component when images change (only if actually different)
  useEffect(() => {
    const currentImagesString = JSON.stringify(currentImages);
    const previousImagesString = JSON.stringify(previousImagesRef.current);

    if (currentImagesString !== previousImagesString) {
      previousImagesRef.current = currentImages;
      onChangeRef.current(currentImages);
    }
  }, [currentImages]);

  // Debug: Log when images are added/removed to help track "ghost" images
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const imageCounts = {
        exterior: (currentImages.exterior || []).length,
        interior: (currentImages.interior || []).length,
        damage: (currentImages.damage || []).length,
        serviceImages: (currentImages.serviceImages || []).length,
        tiresPrimary: (currentImages.tires?.primary || []).length,
        tiresSecondary: (currentImages.tires?.secondary || []).length,
      };
      // DEBUG: Uncomment for debugging image counts
      // console.log("[VehicleImageUploadSection] Current image counts:", imageCounts);
    }
  }, [currentImages]);

  // Update image files structure when addedImages or other relevant data changes (create mode only)
  useEffect(() => {
    if (onImageFilesChange && !isEditMode) {
      const imageFiles = buildImageFilesStructure();
      onImageFilesChange(imageFiles);
    }
  }, [
    addedImages,
    addedServiceDetails,
    buildImageFilesStructure,
    onImageFilesChange,
    isEditMode,
  ]);

  // Use image upload hook
  const { processImageFile, openFilePickerForFiles, openFilePicker } =
    useImageUpload({
      language,
      currentImages,
      setCurrentImages,
      setAddedImages,
      setRemovedImages,
      setHasChanges,
      setExpandedComments,
      uploadingImages,
      setUploadingImages,
      setUploadingThumbnail,
      setUploadingRegularExterior,
      setUploadingEngineVideo,
      isEditMode,
      testId,
      processedImagesRef,
    });

  const removeListingForQueueRef = useRef<
    (item: ListingPhotoQueueItem) => void
  >(() => {});

  const listingPhotoQueue = useListingPhotoUploadQueue({
    currentImages,
    processImageFile,
    onOrphanedUpload: (item, uploadedImageId) => {
      removeListingForQueueRef.current({
        id: "",
        sessionId: "",
        blob: new Blob(),
        previewUrl: "",
        status: "done",
        section: item.section,
        tireType: item.tireType,
        isThumbnail: item.isThumbnail,
        photoIndex: item.photoIndex,
        uploadedImageId,
      });
    },
  });

  const photoSessionLabels = (
    lp.images as {
      photoSession?: Record<string, string>;
    }
  ).photoSession;

  const [photoSessionOpen, setPhotoSessionOpen] = useState(false);
  const [photoSessionConfig, setPhotoSessionConfig] = useState<{
    section: PhotoSessionSection;
    tireType?: "primary" | "secondary";
    engineOnly?: boolean;
    sessionId: string;
  } | null>(null);

  const openPhotoSession = useCallback(
    (
      section: PhotoSessionSection,
      tireType?: "primary" | "secondary",
      opts?: { engineOnly?: boolean },
    ) => {
      const engineOnly = opts?.engineOnly === true;
      if (engineOnly) {
        const hasEngine = (currentImages.exterior || []).some((img) =>
          isVideoMedia(img.type, img.url),
        );
        if (hasEngine) {
          toast.error(
            (
              lp.images as { engineVideo?: { alreadyExists?: string } }
            ).engineVideo?.alreadyExists ??
              "Motorvideo finns redan. Ta bort den först för att filma om.",
          );
          return;
        }
      } else {
        const uploaded = getSectionImageCount(currentImages, section, tireType);
        const inFlight = listingPhotoQueue.countInFlightForSection(
          section,
          tireType,
        );
        const max = getSectionMax(section);
        if (uploaded + inFlight >= max) {
          const msg = (
            lp.images.toasts?.maxReached ??
            "Max antal bilder ({max}) har redan nåtts för denna sektion"
          ).replace("{max}", String(max));
          toast.error(msg);
          return;
        }
      }
      setPhotoSessionConfig({
        section,
        tireType,
        engineOnly,
        sessionId: `ps-${Date.now()}`,
      });
      setPhotoSessionOpen(true);
    },
    [currentImages, listingPhotoQueue, lp.images],
  );

  const handlePhotoSessionCapture = useCallback(
    (blob: Blob): boolean => {
      if (!photoSessionConfig) {
        psWarn("upload.handleCapture.noConfig");
        return false;
      }
      const { section, tireType, sessionId } = photoSessionConfig;
      const isVideo = (blob.type || "").toLowerCase().startsWith("video/");
      const uploaded = getSectionImageCount(currentImages, section, tireType);
      const sessionOccupied = listingPhotoQueue.queue.filter(
        (i) =>
          i.section === section &&
          i.tireType === tireType &&
          (i.status === "pending" ||
            i.status === "uploading" ||
            i.status === "done"),
      ).length;
      const inFlight = listingPhotoQueue.countInFlightForSection(
        section,
        tireType,
      );
      const max = getSectionMax(section);
      psLog("upload.handleCapture", {
        sessionId,
        section,
        tireType,
        uploaded,
        inFlight,
        sessionOccupied,
        max,
        blobSize: blob.size,
        isVideo,
      });
      if (isVideo) {
        const hasEngine = (currentImages.exterior || []).some((img) =>
          isVideoMedia(img.type, img.url),
        );
        if (section === "exterior" && hasEngine) {
          psWarn("upload.handleCapture.rejected", {
            reason: "engine_video_exists",
          });
          return false;
        }
      } else if (Math.max(uploaded, sessionOccupied) >= max) {
        psWarn("upload.handleCapture.rejected", { reason: "max_reached" });
        return false;
      }
      const enqueuedId = listingPhotoQueue.enqueue(
        sessionId,
        section,
        blob,
        tireType,
      );
      return enqueuedId !== null;
    },
    [photoSessionConfig, currentImages, listingPhotoQueue],
  );

  const photoSessionSectionTitle = photoSessionConfig
    ? (() => {
        const key = getSectionTitleKey(
          photoSessionConfig.section,
          photoSessionConfig.tireType,
        );
        const title = (lp.images as Record<string, unknown>)[key];
        return typeof title === "string" ? title : photoSessionConfig.section;
      })()
    : "";

  const photoSessionItems = photoSessionConfig
    ? listingPhotoQueue.getSessionItems(photoSessionConfig.sessionId)
    : [];

  const removeImage = async (
    section: keyof VehicleTestImages,
    index: number,
    subSection?: "primary" | "secondary",
    imageIdForExterior?: string,
  ) => {
    let removedImageId: string | undefined;
    let imageToRemove: ExteriorImage | DamageImage | FileObject | undefined;

    if (section === "exterior") {
      const exteriorArr = currentImages.exterior || [];
      imageToRemove = imageIdForExterior
        ? exteriorArr.find((img) => img.id === imageIdForExterior)
        : exteriorArr[index];
      removedImageId = imageToRemove?.id;
      const removeBy = imageIdForExterior
        ? (img: { id?: string }) => img.id !== imageIdForExterior
        : (_: unknown, i: number) => i !== index;
      setCurrentImages((prev) => ({
        ...prev,
        exterior: prev.exterior?.filter(removeBy) || [],
      }));
    } else if (section === "interior") {
      imageToRemove = currentImages.interior?.[index];
      removedImageId = imageToRemove?.id;
      setCurrentImages((prev) => ({
        ...prev,
        interior: prev.interior?.filter((_, i) => i !== index) || [],
      }));
    } else if (section === "damage") {
      imageToRemove = currentImages.damage?.[index];
      removedImageId = imageToRemove?.id;
      setCurrentImages((prev) => ({
        ...prev,
        damage: prev.damage?.filter((_, i) => i !== index) || [],
      }));
    } else if (section === "serviceImages") {
      imageToRemove = currentImages.serviceImages?.[index];
      removedImageId = imageToRemove?.id;
      setCurrentImages((prev) => ({
        ...prev,
        serviceImages: prev.serviceImages?.filter((_, i) => i !== index) || [],
      }));
    } else if (section === "tires" && subSection) {
      imageToRemove = currentImages.tires?.[subSection]?.[index];
      removedImageId = imageToRemove?.id;
      setCurrentImages((prev) => ({
        ...prev,
        tires: {
          ...prev.tires,
          [subSection]:
            prev.tires?.[subSection]?.filter((_, i) => i !== index) || [],
        },
      }));
    }

    // In EDIT-mode, don't delete immediately - track for lazy update
    // Images will be deleted when "Spara bildändringar" is clicked
    // In CREATE-mode (no testId), images are just removed from state (no API call needed)

    // Clean up processed images ref + photo-session queue (avoids "Max 1" after re-record)
    if (removedImageId) {
      processedImagesRef.current.delete(removedImageId);
      listingPhotoQueue.removeByUploadedImageId(removedImageId);
    }

    // Track removed images (both edit and create mode)
    // If it's a newly added image (has temp ID), remove it from addedImages
    // If image was already deleted via API, don't add to removedImages (already deleted)
    const isNewlyAdded = removedImageId && removedImageId.startsWith("temp-");

    if (isNewlyAdded) {
      setAddedImages((prev) => {
        // Remove by matching index (for damage) or by section and index
        if (section === "damage") {
          // For damage in create mode, remove by matching the damage image index
          // Find which damage image was removed
          const damageImages =
            currentImages.damage?.filter((d) => d.id?.startsWith("temp-")) ||
            [];
          const removedIndex = damageImages.findIndex(
            (d) => d.id === removedImageId,
          );
          if (removedIndex !== -1) {
            let damageCount = 0;
            return prev.filter((img) => {
              if (img.section === "damage") {
                const shouldKeep = damageCount !== removedIndex;
                damageCount++;
                return shouldKeep;
              }
              return true;
            });
          }
          return prev;
        } else {
          // For other sections, find and remove the matching image by index
          const sectionImages = prev.filter((img) => {
            if (img.section !== section) return false;
            if (section === "tires" && subSection) {
              return img.tireType === subSection;
            }
            return true;
          });

          // Find the index of the removed image in the section
          const sectionIndex =
            section === "exterior"
              ? (currentImages.exterior?.findIndex(
                  (img) => img.id === removedImageId,
                ) ?? -1)
              : section === "interior"
                ? (currentImages.interior?.findIndex(
                    (img) => img.id === removedImageId,
                  ) ?? -1)
                : section === "serviceImages"
                  ? (currentImages.serviceImages?.findIndex(
                      (img) => img.id === removedImageId,
                    ) ?? -1)
                  : -1;

          if (sectionIndex !== -1 && sectionIndex < sectionImages.length) {
            // Remove the image at that index from addedImages
            let currentSectionIndex = 0;
            return prev.filter((img) => {
              if (img.section === section) {
                if (section === "tires" && subSection) {
                  if (img.tireType === subSection) {
                    const shouldKeep = currentSectionIndex !== sectionIndex;
                    currentSectionIndex++;
                    return shouldKeep;
                  }
                  return true;
                } else {
                  const shouldKeep = currentSectionIndex !== sectionIndex;
                  currentSectionIndex++;
                  return shouldKeep;
                }
              }
              return true;
            });
          }
          // Fallback: remove last matching image by section
          let lastMatchingIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].section === section) {
              if (section === "tires" && subSection) {
                if (prev[i].tireType === subSection) {
                  lastMatchingIndex = i;
                  break;
                }
              } else {
                lastMatchingIndex = i;
                break;
              }
            }
          }
          if (lastMatchingIndex !== -1) {
            return prev.filter((_, idx) => idx !== lastMatchingIndex);
          }
        }
        return prev;
      });
    }

    // In edit mode, track removal for lazy update (don't delete immediately)
    // Only track if it's an existing image (not newly added temp image)
    if (isEditMode && !isNewlyAdded && removedImageId) {
      setRemovedImages((prev) => {
        // Avoid duplicates
        if (prev.includes(removedImageId)) {
          return prev;
        }
        return [...prev, removedImageId];
      });
    } else if (
      !isEditMode &&
      testId &&
      removedImageId &&
      !removedImageId.startsWith("temp-")
    ) {
      // In create mode with eager upload (testId exists), delete immediately from AWS
      // Only delete if it's a real image (not temp ID) - real images were uploaded via eager upload
      try {
        await CarTestService.removeCarTestImage(testId, removedImageId);
        toast.success(lp.images.imageRemoved);
      } catch (error) {
        toast.error(lp.images.imageRemoveError);
        // Rollback: add image back to state
        if (section === "exterior") {
          setCurrentImages((prev) => ({
            ...prev,
            exterior: [
              ...(prev.exterior || []),
              imageToRemove as ExteriorImage,
            ],
          }));
        } else if (section === "interior") {
          setCurrentImages((prev) => ({
            ...prev,
            interior: [...(prev.interior || []), imageToRemove as FileObject],
          }));
        } else if (section === "damage") {
          setCurrentImages((prev) => ({
            ...prev,
            damage: [...(prev.damage || []), imageToRemove as DamageImage],
          }));
        } else if (section === "serviceImages") {
          setCurrentImages((prev) => ({
            ...prev,
            serviceImages: [
              ...(prev.serviceImages || []),
              imageToRemove as FileObject,
            ],
          }));
        } else if (section === "tires" && subSection) {
          setCurrentImages((prev) => ({
            ...prev,
            tires: {
              ...prev.tires,
              [subSection]: [
                ...(prev.tires?.[subSection] || []),
                imageToRemove as FileObject,
              ],
            },
          }));
        }
        // Re-add to processed images ref
        if (removedImageId) {
          processedImagesRef.current.add(removedImageId);
        }
        return; // Exit early on error
      }
    } else if (!isEditMode && !removedImageId) {
      // For images without ID in CREATE-mode, use a combination of section, index, and URL as identifier
      const imageUrl = imageToRemove?.url || "";
      const fallbackId = `removed-${section}-${index}-${imageUrl.slice(-10)}`;
      setRemovedImages((prev) => {
        if (prev.includes(fallbackId)) {
          return prev;
        }
        return [...prev, fallbackId];
      });
    }

    setHasChanges(true);
  };

  const removeListingImageForQueueItem = useCallback(
    (item: ListingPhotoQueueItem) => {
      const imageId = item.uploadedImageId;
      if (!imageId) return;

      const images = currentImagesForRemoveRef.current;

      if (item.section === "exterior") {
        void removeImage("exterior", 0, undefined, imageId);
        return;
      }

      const findIndex = (arr?: { id?: string }[]) =>
        arr?.findIndex((img) => img.id === imageId) ?? -1;

      let index = -1;
      if (item.section === "interior") {
        index = findIndex(images.interior);
      } else if (item.section === "damage") {
        index = findIndex(images.damage);
      } else if (item.section === "serviceImages") {
        index = findIndex(images.serviceImages);
      } else if (item.section === "tires" && item.tireType) {
        index = findIndex(images.tires?.[item.tireType]);
      }

      if (index >= 0) {
        void removeImage(item.section, index, item.tireType);
      }
    },
    [removeImage],
  );
  removeListingForQueueRef.current = removeListingImageForQueueItem;

  const handlePhotoSessionRemove = useCallback(
    (id: string) => {
      const item = listingPhotoQueue.queue.find((q) => q.id === id);
      if (!item) return;
      if (item.status === "done" && item.uploadedImageId) {
        removeListingImageForQueueItem(item);
      }
      listingPhotoQueue.remove(id);
    },
    [listingPhotoQueue, removeListingImageForQueueItem],
  );

  const handlePhotoSessionReplace = useCallback(
    (id: string, blob: Blob): boolean => {
      const item = listingPhotoQueue.queue.find((q) => q.id === id);
      if (item?.status === "done" && item.uploadedImageId) {
        removeListingImageForQueueItem(item);
      }
      return listingPhotoQueue.replacePending(id, blob);
    },
    [listingPhotoQueue, removeListingImageForQueueItem],
  );

  const updateDamageComment = useCallback(
    async (index: number, comment: string) => {
      const damageImage = currentImages.damage?.[index];

      // In edit mode, track comments for existing images (with IDs)
      if (
        damageImage?.id &&
        isEditMode &&
        !damageImage.id.startsWith("temp-")
      ) {
        setUpdatedDamageComments((prev) => {
          const existing = prev.find((item) => item.imageId === damageImage.id);
          if (existing) {
            return prev.map((item) =>
              item.imageId === damageImage.id ? { ...item, comment } : item,
            );
          } else {
            return [...prev, { imageId: damageImage.id, comment }];
          }
        });
      }

      // In create mode with eager upload (testId exists), update comment directly in database
      if (
        damageImage?.id &&
        !damageImage.id.startsWith("temp-") &&
        !isEditMode &&
        testId
      ) {
        // Image was uploaded via eager upload and has real ID - update comment in database
        try {
          await CarTestService.updateCarTestImageComment(
            testId,
            damageImage.id,
            comment,
          );
          // Comment is now saved in database, no need to track in state
        } catch (error) {
          toast.error(lp.images.commentSaveError);
        }
      }

      // For temp IDs (not yet persisted), store comment in addedImages so it's included in the next save.
      // This is needed in BOTH create + edit flows (edit mode uploads are also temp until saved).
      if (damageImage?.id?.startsWith("temp-")) {
        const tempId = damageImage.id;
        setAddedImages((prev) =>
          prev.map((img) =>
            img.section === "damage" && img.imageId === tempId
              ? { ...img, comment }
              : img,
          ),
        );
      }

      // Update currentImages for UI display
      setCurrentImages((prev) => ({
        ...prev,
        damage:
          prev.damage?.map((damage, i) =>
            i === index ? { ...damage, comment } : damage,
          ) || [],
      }));
      setHasChanges(true);
    },
    [currentImages.damage, isEditMode, addedImages],
  );

  const removeServiceDetail = (id: string) => {
    if (isEditMode) {
      setRemovedServiceDetails((prev) => [...prev, id]);
    }

    setCurrentImages((prev) => ({
      ...prev,
      serviceDetails: {
        serviceDetails:
          prev.serviceDetails?.serviceDetails?.filter(
            (detail) => detail.id !== id,
          ) || [],
      },
    }));
    setHasChanges(true);
  };

  const toggleCommentExpansion = useCallback(
    (index: number) => {
      setExpandedComments((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
      // When expanding, initialize commentTexts and commentTextsRef with current comment value
      const damageImage = currentImages.damage?.[index];
      const comment = damageImage?.comment || "";
      setCommentTexts((prev) => ({
        ...prev,
        [index]: comment,
      }));
      commentTextsRef.current[index] = comment;
    },
    [currentImages.damage],
  );

  const saveComment = useCallback(
    async (index: number) => {
      const comment = commentTextsRef.current[index] || "";
      await updateDamageComment(index, comment);
      setExpandedComments((prev) => ({
        ...prev,
        [index]: false,
      }));
    },
    [updateDamageComment],
  );

  const cancelComment = useCallback(
    (index: number) => {
      setExpandedComments((prev) => ({
        ...prev,
        [index]: false,
      }));
      // Reset commentTextsRef to original comment value when canceling
      const damageImage = currentImages.damage?.[index];
      const originalComment = damageImage?.comment || "";
      commentTextsRef.current[index] = originalComment;
      setCommentTexts((prev) => ({
        ...prev,
        [index]: originalComment,
      }));
    },
    [currentImages.damage],
  );

  // Session-only rotation: update rotationByImageId (90° steps). Persisted to backend only on "Spara bildändringar".
  const handleRotate = useCallback(
    (image: any, direction: "left" | "right") => {
      const imageId = getStableImageId(image);
      if (!imageId) return;

      const delta = direction === "left" ? -90 : 90;
      setRotationByImageId((prev) => {
        const current = prev[imageId] ?? 0;
        const next = (current + delta + 360) % 360;
        return { ...prev, [imageId]: next === 0 ? 0 : next };
      });
      setHasChanges(true);
    },
    [getStableImageId],
  );

  const getImageRotation = useCallback(
    (imageId: string | number) => rotationByImageId[String(imageId)] ?? 0,
    [rotationByImageId],
  );

  const runSaveImageChanges = async () => {
    if (!onSaveImageChanges || !testId || isSaving) return;
    if (!hasChanges && !hasPendingReordersRef.current) return;
    setIsSaving(true);
    try {
      // Avoid duplicate uploads: one entry per imageId (safeguard at uppladdning)
      const addedImagesDeduped = (() => {
        const seen = new Set<string>();
        return addedImages.filter((img) => {
          const id =
            img.imageId ?? (img as any).id ?? `idx-${addedImages.indexOf(img)}`;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      })();
      // Apply rotation to newly added images (temp-ID) before upload, so "upload + rotate in same session" works
      let imagesToUpload = addedImagesDeduped;
      const tempRotations = Object.entries(rotationByImageId).filter(
        ([id, deg]) => id.startsWith("temp-") && deg !== 0,
      );
      if (tempRotations.length > 0 && addedImagesDeduped.length > 0) {
        const { rotateImageToFile } = await import("@/utils/rotateImage");
        imagesToUpload = await Promise.all(
          addedImagesDeduped.map(async (img) => {
            const id = img.imageId ?? (img as any).id;
            const deg = id != null ? rotationByImageId[id] : undefined;
            if (deg == null || deg === 0) return img;
            const file = img.originalFile ?? img.file;
            if (!file) return img;
            const rotatedFile = await rotateImageToFile(file, deg);
            return {
              ...img,
              file: rotatedFile,
              originalFile: rotatedFile,
            };
          }),
        );
        setRotationByImageId((prev) => {
          const next = { ...prev };
          tempRotations.forEach(([id]) => delete next[id]);
          return next;
        });
      }

      // Create mode: eager upload already persisted images with real picture ids via addCarTestImage.
      // addedImages still tracks them (for full-size preview) — do not send again as addImages or we get duplicates.
      if (!isEditMode) {
        imagesToUpload = imagesToUpload.filter((img) => {
          const raw = img.imageId ?? (img as any).id;
          const id = raw != null ? String(raw) : "";
          if (!id) return true;
          if (id.startsWith("temp-")) return true;
          return false;
        });
      }

      // In edit mode:
      // - addImages: New images added by user are sent as File objects
      // - removeImages: Existing images removed by user are sent as image IDs (strings)
      const addImagesPayload = imagesToUpload.map((img) => ({
        file: img.file, // File object for new images
        section: img.section as
          | "exterior"
          | "interior"
          | "damage"
          | "serviceImages"
          | "tires",
        isThumbnail: img.isThumbnail,
        tireType: img.tireType, // Include tireType for tires section
        comment: img.comment, // Include comment for damage images
      }));

      const updateData: UpdateVehicleTestImagesRequest = {
        testId,
        addImages: addImagesPayload,
        removeImages: removedImages, // Array of image IDs (strings) for existing images to remove
        ...(addedServiceDetails.length > 0 && {
          addServiceDetails: addedServiceDetails,
        }),
        ...(removedServiceDetails.length > 0 && {
          removeServiceDetails: removedServiceDetails,
        }),
        ...(updatedDamageComments.length > 0 && {
          updateDamageComments: updatedDamageComments,
        }),
      };

      const hasBatchImageMutation =
        addImagesPayload.length > 0 ||
        removedImages.length > 0 ||
        addedServiceDetails.length > 0 ||
        removedServiceDetails.length > 0 ||
        updatedDamageComments.length > 0;

      if (hasBatchImageMutation) {
        await onSaveImageChanges(updateData);
      }

      // Apply pending reorders (API call per section; tires = one combined call so position order is correct in CarDetails > Hjul)
      const reorderEntries = Object.entries(pendingReorders).filter(
        ([_, ids]) => ids && ids.length > 0,
      );
      const tirePrimaryIds = pendingReorders.tiresPrimary ?? [];
      const tireSecondaryIds = pendingReorders.tiresSecondary ?? [];
      const hasTireReorder =
        tirePrimaryIds.length > 0 || tireSecondaryIds.length > 0;
      const otherEntries = reorderEntries.filter(
        ([key]) => key !== "tiresPrimary" && key !== "tiresSecondary",
      );
      if (hasTireReorder) {
        // Use primary list if present (it already has primary then secondary), else secondary list
        const tireIds =
          tirePrimaryIds.length > 0 ? tirePrimaryIds : tireSecondaryIds;
        await CarTestService.reorderCarTestImages(testId, tireIds);
      }
      for (const [, ids] of otherEntries) {
        await CarTestService.reorderCarTestImages(testId, ids);
      }

      // Apply session rotations: fetch each image, rotate, replace via API (one by one).
      // Collect all results then apply in ONE setCurrentImages so each slot gets its own url (avoids closure bug where both got same url).
      const rotationEntries = Object.entries(rotationByImageId).filter(
        ([id, deg]) => !id.startsWith("temp-") && deg !== 0,
      );
      const { getCarTestImageDownloadUrl } =
        await import("@/services/carTest/carTestService");
      const { rotateImageToFile } = await import("@/utils/rotateImage");
      const rotationUpdates: Array<{
        imageId: string;
        url: string;
        previewMediaId: string;
        fullSizeMediaId: string;
      }> = [];
      const clearedRotationIds: string[] = [];
      for (const [imageId, degrees] of rotationEntries) {
        setRotatingImageId(imageId);
        try {
          const previewUrl = getCarTestImageDownloadUrl(
            testId,
            imageId,
            "preview",
          );
          const rotatedPreviewFile = await rotateImageToFile(
            previewUrl,
            degrees,
            imageId,
          );
          let rotatedFullSizeFile: File | undefined;
          try {
            const fullSizeUrl = getCarTestImageDownloadUrl(
              testId,
              imageId,
              "fullsize",
            );
            rotatedFullSizeFile = await rotateImageToFile(
              fullSizeUrl,
              degrees,
              imageId,
            );
          } catch (_) {}
          const replaceResult = await CarTestService.replaceCarTestImage(
            testId,
            imageId,
            rotatedPreviewFile,
            rotatedFullSizeFile,
          );
          const {
            url,
            previewMediaId: newPreviewMediaId,
            fullSizeMediaId: newFullSizeMediaId,
          } = replaceResult;
          if (newPreviewMediaId && newFullSizeMediaId) {
            rotationUpdates.push({
              imageId,
              url,
              previewMediaId: newPreviewMediaId,
              fullSizeMediaId: newFullSizeMediaId,
            });
            clearedRotationIds.push(imageId);
          }
        } catch (err) {
          toast.error(
            lp.images?.imageRotationError ??
              "Kunde inte spara rotation. Försök igen.",
          );
        } finally {
          setRotatingImageId(null);
        }
      }
      if (rotationUpdates.length > 0) {
        // One update per imageId (avoid applying same replace twice)
        const rotationUpdatesDeduped = (() => {
          const byId = new Map<string, (typeof rotationUpdates)[0]>();
          rotationUpdates.forEach((u) => byId.set(u.imageId, u));
          return Array.from(byId.values());
        })();

        setPreviewUrls((prev) => {
          const next = new Map(prev);
          rotationUpdatesDeduped.forEach((u) =>
            next.set(u.previewMediaId, u.url),
          );
          return next;
        });
        setCurrentImages((prev) => {
          let next = { ...prev };
          for (const u of rotationUpdatesDeduped) {
            const updatedItem = {
              url: u.url,
              previewMediaId: u.previewMediaId,
              fullSizeMediaId: u.fullSizeMediaId,
              mediaId: u.fullSizeMediaId,
            };
            const patch = (arr: any[] | undefined) =>
              arr?.map((item) =>
                getStableImageId(item) === u.imageId
                  ? { ...item, ...updatedItem }
                  : item,
              );
            if (next.exterior?.some((p) => getStableImageId(p) === u.imageId)) {
              next = {
                ...next,
                exterior: patch(next.exterior) || [],
              };
              continue;
            }
            if (next.interior?.some((p) => getStableImageId(p) === u.imageId)) {
              next = {
                ...next,
                interior: patch(next.interior) || [],
              };
              continue;
            }
            if (next.damage?.some((p) => getStableImageId(p) === u.imageId)) {
              next = { ...next, damage: patch(next.damage) || [] };
              continue;
            }
            if (
              next.serviceImages?.some((p) => getStableImageId(p) === u.imageId)
            ) {
              next = {
                ...next,
                serviceImages: patch(next.serviceImages) || [],
              };
              continue;
            }
            const primary = next.tires?.primary;
            if (primary?.some((p) => getStableImageId(p) === u.imageId)) {
              next = {
                ...next,
                tires: {
                  ...next.tires!,
                  primary: patch(primary) || [],
                  secondary: next.tires?.secondary || [],
                },
              };
              continue;
            }
            const secondary = next.tires?.secondary;
            if (secondary?.some((p) => getStableImageId(p) === u.imageId)) {
              next = {
                ...next,
                tires: {
                  ...next.tires!,
                  primary: next.tires?.primary || [],
                  secondary: patch(secondary) || [],
                },
              };
            }
          }
          // Defensive: ensure no duplicate picture ids in any section after patch
          if (next.exterior?.length)
            next = {
              ...next,
              exterior: dedupeImagesById(next.exterior),
            };
          if (next.interior?.length)
            next = {
              ...next,
              interior: dedupeImagesById(next.interior),
            };
          if (next.damage?.length)
            next = {
              ...next,
              damage: dedupeImagesById(next.damage),
            };
          if (next.serviceImages?.length)
            next = {
              ...next,
              serviceImages: dedupeImagesById(next.serviceImages),
            };
          if (next.tires?.primary?.length)
            next = {
              ...next,
              tires: {
                ...next.tires!,
                primary: dedupeImagesById(next.tires.primary),
              },
            };
          if (next.tires?.secondary?.length)
            next = {
              ...next,
              tires: {
                ...next.tires!,
                secondary: dedupeImagesById(next.tires.secondary),
              },
            };
          return next;
        });
        setRotationByImageId((prev) => {
          const next = { ...prev };
          clearedRotationIds.forEach((id) => delete next[id]);
          return next;
        });
      }

      // Only reset state if save was successful
      setAddedImages([]);
      setRemovedImages([]);
      setAddedServiceDetails([]);
      setRemovedServiceDetails([]);
      setUpdatedDamageComments([]);
      setPendingReorders({});
      setHasChanges(false);
      hasPendingReordersRef.current = false;
      if (testId) onAfterImageSaveComplete?.(testId);
    } catch (error) {
      // Don't reset state on error, so user can try again
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    savePendingImages: () => runSaveImageChanges(),
    clearPendingServiceDetails: () => {
      // After createTest persists service details, drop pending so submit won't re-POST them
      setAddedServiceDetails([]);
    },
  }));

  useEffect(() => {
    onImageDirtyChange?.(hasChanges);
  }, [hasChanges, onImageDirtyChange]);

  // Validation logic - using extracted functions
  const validationErrors = getValidationErrors(currentImages, lp.images);
  const sectionValid = isSectionValid(currentImages);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="sr-only">{lp.images.title}</h2>
        <div className="flex flex-col gap-2 justify-center items-stretch mb-2 max-w-md mx-auto">
          {hasPhotoGuide && photoGuide && (
            <button
              type="button"
              onClick={() => setShowPhotoGuideVideo(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold focus:outline-none transition-colors"
              aria-label={lp.images.photoGuideTitle}
            >
              <Play className="w-5 h-5" />
              {lp.images.photoGuideTitle}
            </button>
          )}
          <Dialog open={photoGuideOpen} onOpenChange={setPhotoGuideOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 font-semibold focus:outline-none transition-colors"
                aria-label={lp.images.photoGuideButton}
              >
                <Info className="w-5 h-5" />
                {lp.images.photoGuideButton}
              </button>
            </DialogTrigger>
            <DialogContent
              {...photoGuideSheetProps}
              className={cn(
                photoGuideSheetProps.className,
                "max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-6",
              )}
              aria-describedby={undefined}
            >
              <DialogTitle className="sr-only">
                {lp.images.photoGuideButton}
              </DialogTitle>
              <div className="pr-4">
                <ModalInfoVehicleImages />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {showPhotoGuideVideo && hasPhotoGuide && photoGuide && (
        <VideoPlayer
          videoUrl={
            normalizeSupportGuideVideoUrl(photoGuide.videoUrl) ??
            photoGuide.videoUrl ??
            ""
          }
          title={lp.images.photoGuideTitle}
          onClose={() => setShowPhotoGuideVideo(false)}
        />
      )}

      {validationErrors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-300 font-semibold mb-2">
            {lp.images.missingRequirements}
          </h3>
          <ul className="text-red-700 dark:text-red-400 text-sm space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <Accordion
        type="multiple"
        defaultValue={["exterior"]}
        className="space-y-4"
      >
        <AccordionItem value="exterior" className="border rounded-lg px-4">
          <AccordionTrigger className="font-semibold flex items-center justify-between w-full py-2">
            <div className="flex items-center gap-2">
              {lp.images.exterior}
              <Badge variant="outline">
                {(() => {
                  const exteriorPhotos = (currentImages.exterior || []).filter(
                    (img) =>
                      !is360ExteriorFrame(img) &&
                      !isVideoMedia(img.type, img.url),
                  );
                  return `${exteriorPhotos.length}/${EXTERIOR_MAX_REGULAR}`;
                })()}
              </Badge>
              {(() => {
                const exteriorPhotos = (currentImages.exterior || []).filter(
                  (img) =>
                    !is360ExteriorFrame(img) &&
                    img.url &&
                    !isVideoMedia(img.type, img.url),
                );
                if (exteriorPhotos.length === 0) {
                  return (
                    <Badge variant="destructive">{lp.images.required}</Badge>
                  );
                }
                return null;
              })()}
            </div>
            <ImageSectionTips section="exterior" />
          </AccordionTrigger>
          <AccordionContent>
            <ExteriorSection
              exteriorImages={(currentImages.exterior || []).filter(
                (img) => !is360ExteriorFrame(img),
              )}
              uploadingExterior={uploadingRegularExterior}
              uploadingEngineVideo={uploadingEngineVideo}
              onImageClick={(image, alt) => {
                handleImageClick(image, alt);
              }}
              getThumbnailUrl={getThumbnailUrl}
              onRemoveImage={removeImage}
              onRotate={handleRotate}
              rotatingImageId={rotatingImageId}
              getImageRotation={getImageRotation}
              onFileSelect={(files) =>
                openFilePickerForFiles(files, "exterior", false, undefined)
              }
              onSelect={() =>
                openFilePicker("exterior", false, undefined, false)
              }
              onCameraSelect={() => openPhotoSession("exterior")}
              onEngineVideoCameraSelect={() =>
                openPhotoSession("exterior", undefined, { engineOnly: true })
              }
              onVideoSelect={(file) =>
                void openFilePickerForFiles([file], "exterior", false, undefined)
              }
              onReorder={
                testId
                  ? (newOrder) => {
                      const frames360 = (currentImages.exterior || []).filter(
                        (img) => is360ExteriorFrame(img),
                      );
                      const ordered = applyExteriorThumbnailFlags(newOrder);
                      const newExterior = [...ordered, ...frames360];
                      reorderSection("exterior", ordered, (prev) => ({
                        ...prev,
                        exterior: newExterior,
                      }));
                    }
                  : undefined
              }
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="interior" className="border rounded-lg px-4">
          <AccordionTrigger className="font-semibold flex items-center justify-between w-full py-2">
            <div className="flex items-center gap-2">
              {lp.images.interior}
              <Badge variant="outline">
                {(currentImages.interior || []).length}/15
              </Badge>
              {(() => {
                const hasInterior = currentImages.interior?.some(
                  (img) => img.url,
                );
                if (!hasInterior) {
                  return (
                    <Badge variant="destructive">{lp.images.required}</Badge>
                  );
                }
                return null;
              })()}
            </div>
            <ImageSectionTips section="interior" />
          </AccordionTrigger>
          <AccordionContent>
            <SimpleImageSection
              title=""
              section="interior"
              maxImages={15}
              images={currentImages.interior || []}
              uploadingImages={uploadingImages}
              onImageClick={(image, alt) => {
                handleImageClick(image, alt);
              }}
              getThumbnailUrl={getThumbnailUrl}
              onRemoveImage={removeImage}
              onRotate={handleRotate}
              rotatingImageId={rotatingImageId}
              getImageRotation={getImageRotation}
              onReorder={
                testId
                  ? (newOrder) =>
                      reorderSection("interior", newOrder, (prev) => ({
                        ...prev,
                        interior: newOrder,
                      }))
                  : undefined
              }
              onFileSelect={(files) =>
                openFilePickerForFiles(files, "interior", false, undefined)
              }
              onSelect={() =>
                openFilePicker("interior", false, undefined, false)
              }
              onCameraSelect={() => openPhotoSession("interior")}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="damage" className="border rounded-lg px-4">
          <AccordionTrigger className="font-semibold flex items-center justify-between w-full py-2">
            <div className="flex items-center gap-2">
              {lp.images.damage}
              <Badge variant="outline">
                {(currentImages.damage || []).length}/15
              </Badge>
            </div>
            <ImageSectionTips section="damage" />
          </AccordionTrigger>
          <AccordionContent>
            <DamageSection
              damageImages={currentImages.damage || []}
              uploadingImages={uploadingImages}
              expandedComments={expandedComments}
              commentTextsRef={commentTextsRef}
              onImageClick={(image, alt) => {
                handleImageClick(image, alt);
              }}
              getThumbnailUrl={getThumbnailUrl}
              onRemoveImage={removeImage}
              onRotate={handleRotate}
              rotatingImageId={rotatingImageId}
              getImageRotation={getImageRotation}
              onToggleComment={toggleCommentExpansion}
              onSaveComment={saveComment}
              onCancelComment={cancelComment}
              onReorder={
                testId
                  ? (newOrder) =>
                      reorderSection("damage", newOrder, (prev) => ({
                        ...prev,
                        damage: newOrder,
                      }))
                  : undefined
              }
              onFileSelect={(files) =>
                openFilePickerForFiles(files, "damage", false, undefined)
              }
              onSelect={() => openFilePicker("damage", false, undefined, false)}
              onCameraSelect={() => openPhotoSession("damage")}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="service" className="border rounded-lg px-4">
          <AccordionTrigger className="font-semibold flex items-start sm:items-center justify-between w-full py-2">
            <div className="flex items-center gap-2">
              {lp.images.service}
              <Badge variant="outline">
                {(currentImages.serviceImages || []).length}/15
              </Badge>
              {(currentImages.serviceDetails?.serviceDetails || []).length >
                0 && (
                <Badge variant="secondary" className="ml-2">
                  {(currentImages.serviceDetails?.serviceDetails || []).length}{" "}
                  {lp.images.details}
                </Badge>
              )}
            </div>
            <ImageSectionTips section="service" />
          </AccordionTrigger>
          <AccordionContent>
            <ServiceSection
              serviceImages={currentImages.serviceImages || []}
              serviceDetails={
                currentImages.serviceDetails?.serviceDetails || []
              }
              uploadingImages={uploadingImages}
              onImageClick={(image, alt) => {
                handleImageClick(image, alt);
              }}
              getThumbnailUrl={getThumbnailUrl}
              onRemoveImage={removeImage}
              onRotate={handleRotate}
              rotatingImageId={rotatingImageId}
              getImageRotation={getImageRotation}
              onReorder={
                testId
                  ? (newOrder) =>
                      reorderSection("serviceImages", newOrder, (prev) => ({
                        ...prev,
                        serviceImages: newOrder,
                      }))
                  : undefined
              }
              onRemoveServiceDetail={removeServiceDetail}
              onEditServiceDetail={(detail) => {
                setEditingServiceDetail(detail);
                setShowServiceDetailDialog(true);
              }}
              onFileSelect={(files) =>
                openFilePickerForFiles(files, "serviceImages", false, undefined)
              }
              onSelect={() =>
                openFilePicker("serviceImages", false, undefined, false)
              }
              onCameraSelect={() => openPhotoSession("serviceImages")}
            />
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingServiceDetail(null);
                  setShowServiceDetailDialog(true);
                }}
                className="w-full sm:w-auto bg-accent text-black hover:bg-accent/80 hover:text-black"
              >
                <Calendar className="h-4 w-4 mr-2" />
                {isMobile
                  ? lp.images.addServiceDetailsShort
                  : lp.images.addServiceDetails}
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="tires" className="border rounded-lg px-4">
          <AccordionTrigger className="font-semibold flex items-center justify-between w-full py-2">
            <div className="flex items-center gap-2">
              {lp.images.wheels}
              <Badge variant="outline">
                {lp.images.primary}:{" "}
                {(currentImages.tires?.primary || []).length}/5,
                {lp.images.secondary}:{" "}
                {(currentImages.tires?.secondary || []).length}/5
              </Badge>
            </div>
            <ImageSectionTips section="tires" />
          </AccordionTrigger>
          <AccordionContent>
            <TireSection
              primaryTires={currentImages.tires?.primary || []}
              secondaryTires={currentImages.tires?.secondary || []}
              uploadingImages={uploadingImages}
              onImageClick={(image, alt) => {
                handleImageClick(image, alt);
              }}
              getThumbnailUrl={getThumbnailUrl}
              onRemoveImage={removeImage}
              onRotate={handleRotate}
              rotatingImageId={rotatingImageId}
              getImageRotation={getImageRotation}
              onReorderPrimary={
                testId
                  ? (newOrder) =>
                      reorderSection(
                        "tires",
                        newOrder,
                        (prev) => ({
                          ...prev,
                          tires: {
                            ...prev.tires,
                            primary: newOrder,
                            secondary: prev.tires?.secondary || [],
                          },
                        }),
                        "primary",
                      )
                  : undefined
              }
              onReorderSecondary={
                testId
                  ? (newOrder) =>
                      reorderSection(
                        "tires",
                        newOrder,
                        (prev) => ({
                          ...prev,
                          tires: {
                            ...prev.tires,
                            primary: prev.tires?.primary || [],
                            secondary: newOrder,
                          },
                        }),
                        "secondary",
                      )
                  : undefined
              }
              onPrimaryFileSelect={(files) =>
                openFilePickerForFiles(files, "tires", false, "primary")
              }
              onPrimarySelect={() =>
                openFilePicker("tires", false, "primary", false)
              }
              onPrimaryCameraSelect={() => openPhotoSession("tires", "primary")}
              onSecondaryFileSelect={(files) =>
                openFilePickerForFiles(files, "tires", false, "secondary")
              }
              onSecondarySelect={() =>
                openFilePicker("tires", false, "secondary", false)
              }
              onSecondaryCameraSelect={() =>
                openPhotoSession("tires", "secondary")
              }
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Image preview – same fullscreen viewer as CarImageGallery */}
      <FullscreenGallery
        images={lightboxUrls}
        mediaTypes={lightboxMediaTypes}
        title={lp.images.imagePreview}
        currentIndex={selectedImage?.index ?? 0}
        open={!!selectedImage}
        onOpenChange={(open) => {
          if (!open) closeImagePreview();
        }}
        onNavigate={(direction) => {
          if (direction === "prev") void navigatePrev();
          else void navigateNext();
        }}
        onSelect={handleFullscreenSelect}
      />

      {/* Service Detail Dialog */}
      <ServiceDetailsDialog
        isOpen={showServiceDetailDialog}
        onClose={() => {
          setShowServiceDetailDialog(false);
          setEditingServiceDetail(null);
        }}
        initialData={editingServiceDetail}
        onSave={(serviceDetail) => {
          if (editingServiceDetail) {
            // Editing existing detail - remove old and add updated
            if (isEditMode) {
              setRemovedServiceDetails((prev) => [
                ...prev,
                editingServiceDetail.id,
              ]);
            }
            setCurrentImages((prev) => ({
              ...prev,
              serviceDetails: {
                serviceDetails: (prev.serviceDetails?.serviceDetails || [])
                  .filter((detail) => detail.id !== editingServiceDetail.id)
                  .concat(serviceDetail),
              },
            }));
            // Add updated detail to addedServiceDetails
            setAddedServiceDetails((prev) => [...prev, serviceDetail]);
          } else {
            // Creating new detail
            setAddedServiceDetails((prev) => [...prev, serviceDetail]);
            setCurrentImages((prev) => ({
              ...prev,
              serviceDetails: {
                serviceDetails: [
                  ...(prev.serviceDetails?.serviceDetails || []),
                  serviceDetail,
                ],
              },
            }));
          }
          setShowServiceDetailDialog(false);
          setEditingServiceDetail(null);
          setHasChanges(true);
        }}
      />

      {/* Navigation */}
      <div className="flex justify-end gap-2">
        {/* Edit mode OR Create mode with testId: show "Spara bildändringar" so rotation/reorder/add/remove get sent */}
        {(isEditMode || testId) && !hideIntegratedImageSaveButton && (
          <Button
            onClick={runSaveImageChanges}
            disabled={
              isSaving || (!hasChanges && !hasPendingReordersRef.current)
            }
            className="bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {lp.widget.saving}
              </>
            ) : (
              lp.images.saveImageChanges
            )}
          </Button>
        )}
        <Button
          onClick={async () => {
            const toApply = Object.entries(rotationByImageId).filter(
              ([id, deg]) => deg !== 0 && id.startsWith("temp-"),
            );
            if (toApply.length > 0 && addedImages.length > 0) {
              const { rotateImageToFile } = await import("@/utils/rotateImage");
              const rotated = await Promise.all(
                addedImages.map(async (img) => {
                  const id = img.imageId ?? (img as any).id;
                  const deg = id != null ? rotationByImageId[id] : undefined;
                  if (deg == null || deg === 0) return img;
                  const file = img.originalFile ?? img.file;
                  if (!file) return img;
                  const rotatedFile = await rotateImageToFile(file, deg);
                  return {
                    ...img,
                    file: rotatedFile,
                    originalFile: rotatedFile,
                  };
                }),
              );
              setAddedImages(rotated);
              setRotationByImageId((prev) => {
                const next = { ...prev };
                toApply.forEach(([id]) => delete next[id]);
                return next;
              });
            }
            const needsPersist =
              (hasChanges || hasPendingReordersRef.current) &&
              !!onSaveImageChanges &&
              !!testId;
            if (needsPersist) {
              setIsSavingBeforeNext(true);
              try {
                await runSaveImageChanges();
              } finally {
                setIsSavingBeforeNext(false);
              }
            }
            await Promise.resolve(onNext());
          }}
          disabled={!sectionValid || isSavingBeforeNext || isSaving}
          className="bg-blue-500 hover:bg-blue-600"
        >
          {isSavingBeforeNext ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {lp.widget?.savingChangesBeforeNext ??
                lp.widget?.saving ??
                "Sparar…"}
            </>
          ) : (
            lp.vehicleTest.next
          )}
        </Button>
      </div>

      {photoSessionLabels && photoSessionConfig && (
        <PhotoSessionModal
          open={photoSessionOpen}
          onOpenChange={(open) => {
            setPhotoSessionOpen(open);
            // Keep sessionId briefly so in-flight Klar-while-recording capture
            // can still enqueue; clear on next tick after close.
            if (!open) {
              window.setTimeout(() => setPhotoSessionConfig(null), 600);
            }
          }}
          sectionTitle={
            photoSessionConfig.engineOnly
              ? ((lp.images as { engineVideo?: { title?: string } }).engineVideo
                  ?.title ?? "Motorvideo")
              : photoSessionSectionTitle
          }
          section={photoSessionConfig.section}
          tireType={photoSessionConfig.tireType}
          engineOnly={photoSessionConfig.engineOnly}
          currentImages={currentImages}
          sessionId={photoSessionConfig.sessionId}
          sessionItems={photoSessionItems}
          onCapture={handlePhotoSessionCapture}
          onReplace={handlePhotoSessionReplace}
          onRemove={handlePhotoSessionRemove}
          onRetry={listingPhotoQueue.retry}
          countInFlightForSection={listingPhotoQueue.countInFlightForSection(
            photoSessionConfig.section,
            photoSessionConfig.tireType,
          )}
          labels={{
            takePhoto: photoSessionLabels.takePhoto ?? "Take photo",
            done: photoSessionLabels.done ?? "Done",
            countOfMax: photoSessionLabels.countOfMax ?? "{current} of {max}",
            maxReached: photoSessionLabels.maxReached ?? "Maximum {max} images",
            startCamera: photoSessionLabels.startCamera ?? "Start camera",
            cameraStarting:
              photoSessionLabels.cameraStarting ?? "Starting camera…",
            cameraError: photoSessionLabels.cameraError ?? "Camera error",
            fallbackLabel: photoSessionLabels.fallbackLabel ?? "Upload instead",
            captureHint: photoSessionLabels.captureHint ?? "",
            statusPending: photoSessionLabels.statusPending ?? "Waiting",
            statusUploading: photoSessionLabels.statusUploading ?? "Uploading",
            statusDone: photoSessionLabels.statusDone ?? "Done",
            statusFailed: photoSessionLabels.statusFailed ?? "Failed",
            removePhoto: photoSessionLabels.removePhoto ?? "Remove",
            retake: photoSessionLabels.retake ?? "Retake",
            nextPhoto: photoSessionLabels.nextPhoto ?? "Next photo",
            retry: photoSessionLabels.retry ?? "Retry",
            stripPlaceholder:
              photoSessionLabels.stripPlaceholder ??
              "Your captured photos will appear here",
            landscapeGuide:
              photoSessionLabels.landscapeGuide ??
              "Keep the entire vehicle inside the frame",
            landscapeFormatHint:
              photoSessionLabels.landscapeFormatHint ??
              "Use landscape orientation",
            processingPhoto:
              photoSessionLabels.processingPhoto ?? "Processing photo…",
            rotatePhoneHint:
              photoSessionLabels.rotatePhoneHint ??
              "Landscape is required for exterior and interior photos only. Rotate your phone horizontally. Other categories (damage, service, wheels) work in portrait or landscape.",
            rotateRequiredTitle:
              photoSessionLabels.rotateRequiredTitle ??
              "Rotate horizontally (exterior/interior)",
            hideGuide: photoSessionLabels.hideGuide ?? "Hide guide",
            showGuide: photoSessionLabels.showGuide ?? "Show guide",
            cameraNotReady:
              photoSessionLabels.cameraNotReady ??
              "Camera not ready – try again in a moment",
            sectionShotTitle:
              photoSessionLabels.sectionShotTitle ?? "{section} – {shot}",
            exteriorGuideStep:
              photoSessionLabels.exteriorGuideStep ?? "Guide {step} of {max}",
            interiorGuideStep:
              photoSessionLabels.interiorGuideStep ?? "Guide {step} of {max}",
            guidedOptionalHint:
              photoSessionLabels.guidedOptionalHint ??
              photoSessionLabels.exteriorOptionalHint ??
              "Optional angle",
            guidedOptionalSub:
              photoSessionLabels.guidedOptionalSub ??
              photoSessionLabels.exteriorOptionalSub ??
              "Photo {current} of {max} – no required framing",
            exteriorOptionalHint:
              photoSessionLabels.exteriorOptionalHint ?? "Optional angle",
            exteriorOptionalSub:
              photoSessionLabels.exteriorOptionalSub ??
              "Photo {current} of {max} – no required framing",
            exteriorGuides: (
              photoSessionLabels as {
                exteriorGuides?: Record<string, string>;
              }
            ).exteriorGuides,
            interiorGuides: (
              photoSessionLabels as {
                interiorGuides?: Record<string, string>;
              }
            ).interiorGuides,
          }}
          maxReachedToast={
            lp.images.toasts?.maxReached ??
            "Max antal bilder ({max}) har redan nåtts för denna sektion"
          }
        />
      )}
    </div>
  );
});

export default VehicleImageUploadSection;
